import React from "react";
import { Box } from "@mui/material";
import {
  certificatePageSize,
  certificatePaperColour,
} from "../../../domain/certificates/certificateTemplates";
import { CentredLayout, type CertificateDesign } from "./certificateParts";
import { pointsAround, starPoints } from "./ornamentShapes";
import { Trophy } from "./Trophy";

/** The scalloped panel's straight edge, this far in from the page edge; its scallops reach out past it. */
const PANEL_INSET = 64;
const SCALLOP_RADIUS = 22;
const SCALLOP_SPACING = 40;

/** Wide diagonal stripes in shades of the two colours, over a pale wash of the accent. */
const StripedBackground: React.FC<{
  width: number;
  height: number;
  primary: string;
  accent: string;
}> = ({ width, height, primary, accent }) => {
  const diagonal = Math.hypot(width, height);
  const stripes: { colour: string; opacity: number; size: number }[] = [
    { colour: primary, opacity: 0.9, size: 1 },
    { colour: "#ffffff", opacity: 0.35, size: 0.6 },
    { colour: accent, opacity: 1, size: 1.2 },
    { colour: primary, opacity: 0.5, size: 0.8 },
    { colour: "#ffffff", opacity: 0.25, size: 1 },
    { colour: accent, opacity: 0.8, size: 0.7 },
    { colour: primary, opacity: 0.8, size: 1.1 },
    { colour: "#ffffff", opacity: 0.4, size: 0.9 },
  ];
  const unit = diagonal / stripes.reduce((sum, { size }) => sum + size, 0);
  let x = width / 2 - diagonal / 2;
  return (
    <>
      <rect width={width} height={height} fill={accent} opacity={0.45} />
      <g transform={`rotate(-35 ${width / 2} ${height / 2})`}>
        {stripes.map(({ colour, opacity, size }, i) => {
          const stripe = (
            <rect
              key={i}
              x={x}
              y={height / 2 - diagonal / 2}
              width={unit * size + 1}
              height={diagonal}
              fill={colour}
              opacity={opacity}
            />
          );
          x += unit * size;
          return stripe;
        })}
      </g>
      {[
        [0.85, 0.06, 26],
        [0.93, 0.34, 20],
        [0.12, 0.92, 18],
        [0.94, 0.94, 24],
      ].map(([cx, cy, r]) => (
        <circle
          key={`${cx},${cy}`}
          cx={width * cx}
          cy={height * cy}
          r={r}
          fill="#ffffff"
          fillOpacity={0.2}
          stroke="#ffffff"
          strokeOpacity={0.7}
          strokeWidth={2}
        />
      ))}
    </>
  );
};

/** White-edged stars round the page, outside the panel, in turns of size and colour. */
const EdgeStars: React.FC<{
  width: number;
  height: number;
  primary: string;
  accent: string;
}> = ({ width, height, primary, accent }) => (
  <>
    {pointsAround(width, height, 28, 120).map((point, i) => {
      const size = [16, 11, 19, 13][i % 4];
      const fill = [primary, "#ffffff", accent, "#ffffff"][i % 4];
      return (
        <polygon
          key={i}
          points={starPoints(5, size, size * 0.48)}
          transform={`translate(${point.x} ${point.y}) rotate(${(i * 17) % 30 - 15})`}
          fill={fill}
          fillOpacity={fill === "#ffffff" ? 0.45 : 1}
          stroke="#ffffff"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      );
    })}
  </>
);

/**
 * Lagoon: a scalloped white panel on teal stripes, with white-edged stars
 * round it and a trophy above the signatures.
 */
export const lagoonDesign: CertificateDesign = {
  bodyFont: "nunito",
  Layout: ({ template, text, orientation }) => {
    const { primaryColour: primary, accentColour: accent } = template;
    const { width, height } = certificatePageSize(orientation);
    const paper = certificatePaperColour(template);
    return (
      <CentredLayout
        template={template}
        text={text}
        orientation={orientation}
        inset={{
          landscape: "80px 112px 76px",
          portrait: "96px 100px 88px",
        }}
        centrepiece={<Trophy colour={accent} />}
        frame={
          <Box
            component="svg"
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            sx={{ position: "absolute", inset: 0, width, height }}
          >
            <StripedBackground
              width={width}
              height={height}
              primary={primary}
              accent={accent}
            />
            <EdgeStars width={width} height={height} primary={primary} accent={accent} />
            {/* The panel: a rectangle with a scallop all along its edge. */}
            <g fill={paper}>
              <rect
                x={PANEL_INSET}
                y={PANEL_INSET}
                width={width - PANEL_INSET * 2}
                height={height - PANEL_INSET * 2}
              />
              {pointsAround(width, height, PANEL_INSET, SCALLOP_SPACING).map(
                ({ x, y }) => (
                  <circle key={`${x},${y}`} cx={x} cy={y} r={SCALLOP_RADIUS} />
                ),
              )}
            </g>
          </Box>
        }
      />
    );
  },
};
